/**
 * KAN-880: общие фильтры доски и таблицы CRM в адресе: ?search=,
 * ?manager=me|<uuid>|none|all. Переживают перезагрузку и смену ?view=. params - то, что
 * уходит в запросы доски и таблицы (crmLeadFilterParams), стабильная ссылка для ключа кеша.
 * manager=me у входа без своей записи менеджера игнорируется (withoutForeignMe).
 * KAN-909: менеджер по умолчанию - свой (useManagerFilterChoice, место 'crm', общее
 * у таблицы, доски и Лидов по дням); выбор пишется в адрес (Все = manager=all) и
 * запоминается. Флажка stale больше нет.
 * Менеджер, которого backend не принял (удалён, 400 по полю manager), сбрасывается:
 * useDropRejectedCrmManager забывает выбор и убирает manager из адреса.
 * KAN-910: адрес - useUrlState (replace); поиск пишется после дебаунса (CrmLeadFilters).
 * Нет ?manager= - итоговое умолчание KAN-909 (запомненный выбор, свой менеджер или all)
 * сразу пишется в адрес, как только загружен пользователь: ссылка полная, у получателя
 * то же значение.
 * KAN-923: умолчание KAN-909 (запомненный выбор, свой менеджер) только у роли MANAGER.
 * У остальных ролей нет ?manager= (меню, дашборд, ссылка без параметров) - всегда
 * "Все менеджеры": прошлый выбор вроде "Без менеджера" не подставляется. Значение
 * держит адрес (обновление страницы, ссылки), в хранилище выбор не пишется.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isManagerUser } from '@/lib/managerAccess';
import { useManagerFilterChoice } from '@/hooks/useManagerFilterChoice';
import { useUrlDefaults, useUrlState } from '@/hooks/useUrlState';
import {
  CRM_FILTER_PARAM,
  MANAGER_FILTER_ALL,
  MANAGER_FILTER_ME,
  crmLeadFilterParams,
  parseCrmLeadFilters,
  withoutForeignMe,
} from '@/lib/crmLeadFilters';
import { isParamError } from '@/lib/crmLeads';
import type { ManagerChoiceValues } from '@/lib/managerFilterChoice';
import type { CrmLeadFilterParams, CrmLeadFilters } from '@/types/crmPipeline';

export interface CrmLeadFiltersState {
  filters: CrmLeadFilters;
  params: CrmLeadFilterParams;
  /** KAN-909: своя запись менеджера у входа (пункт "Мои", кнопка "На меня"). */
  hasOwnManager: boolean;
  setSearch: (value: string) => void;
  /** null - "Все менеджеры" (?manager=all). */
  setManager: (value: string | null) => void;
  /** KAN-909: забыть выбор менеджера и убрать его из адреса (снова умолчание). */
  dropManager: () => void;
}

const CRM_MANAGER_VALUES: ManagerChoiceValues = { all: MANAGER_FILTER_ALL, mine: () => MANAGER_FILTER_ME };

export function useCrmLeadFilters(): CrmLeadFiltersState {
  const { params: searchParams, setParam } = useUrlState();
  const search = searchParams.get(CRM_FILTER_PARAM.search) ?? '';
  const managerInUrl = searchParams.get(CRM_FILTER_PARAM.manager);
  const { data: user } = useCurrentUser();
  const remembers = isManagerUser(user);
  const choice = useManagerFilterChoice(
    'crm',
    CRM_MANAGER_VALUES,
    managerInUrl ?? (user && !remembers ? MANAGER_FILTER_ALL : null)
  );
  const manager = choice.value;
  const hasOwnManager = user ? choice.ownManagerId !== null : null;

  // Умолчание менеджера зависит от входа: пишется в адрес только после загрузки пользователя.
  useUrlDefaults([{ key: CRM_FILTER_PARAM.manager, value: manager }], !!user);

  const filters = useMemo(
    () =>
      withoutForeignMe(
        parseCrmLeadFilters(
          new URLSearchParams({ [CRM_FILTER_PARAM.search]: search, [CRM_FILTER_PARAM.manager]: manager })
        ),
        hasOwnManager
      ),
    [search, manager, hasOwnManager]
  );
  const params = useMemo(() => crmLeadFilterParams(filters), [filters]);

  const { choose, clear } = choice;
  return {
    filters,
    params,
    hasOwnManager: hasOwnManager === true,
    setSearch: useCallback((value: string) => setParam(CRM_FILTER_PARAM.search, value.trim() ? value : null), [setParam]),
    setManager: useCallback(
      (value: string | null) => {
        const next = value ?? MANAGER_FILTER_ALL;
        if (remembers) choose(next);
        setParam(CRM_FILTER_PARAM.manager, next);
      },
      [remembers, choose, setParam]
    ),
    dropManager: useCallback(() => {
      clear();
      setParam(CRM_FILTER_PARAM.manager, null);
    }, [clear, setParam]),
  };
}

/**
 * KAN-909: запрос CRM (таблица, доска, Лиды по дням) получил 400 по полю manager
 * (запомненный или пришедший в адресе менеджер удалён): выбор сбрасывается к умолчанию.
 */
export function useDropRejectedCrmManager(
  error: unknown,
  state: Pick<CrmLeadFiltersState, 'filters' | 'dropManager'>
): void {
  const rejected = !!state.filters.manager && isParamError(error, 'manager');
  const { dropManager } = state;
  useEffect(() => {
    if (rejected) dropManager();
  }, [rejected, dropManager]);
}
